import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../../api/client';
import { normalizeSkillTreeDefinition } from '../SkillTreeMap/SkillTreeMap';
import { stowProdHiddenNodes } from './stowedNodes';
import ProgressMap from '../ProgressMap/ProgressMap';
import { useMapOrbTrayRect } from '../MapOrbTray/MapOrbTrayLayout';
import { getReservoir } from '../../api/golden';
import { useSkillTreeToast } from './SkillTreeToastContext';
import { skillTreeToMapData, computeHtnRegionLayout, normalizeOwnableSlug } from './skillTreeLayout';
import './SkillTreeViewport.css';

async function fetchSkillTreeState() {
  const { data } = await apiClient.get('/api/skill-tree/player-state');
  return data;
}

export default function SkillTreeViewport({
  onPortal,
  onExit,
  initialRegion = 'main',
  onRegionChange,
  // Lock the viewport to its initial region: omit the HTN "Back to Tree"
  // portal and refuse any region-swap back to 'main'. Used by the standalone
  // /play demo so it stays focused on the antihypertensives area.
  lockRegion = false,
  // Guided first-equip gate: an array of HTN drug slugs to narrow the flower to.
  // When set, ONLY these drug cards stay lit + equippable in the HTN region;
  // every other drug is dimmed + padlocked. Non-HTN regions are never affected.
  equipWhitelist = null,
  // CSS selectors for bottom-anchored HUD that overlays this viewport and must
  // not be laid out under. The layout has always reserved the MapOrbTray's
  // occlusion (skillTreeLayout.js PAD_BOTTOM); a host with different bottom
  // chrome had no way to say so, and the standalone HTN surface has three such
  // elements, so its lowest drug row rendered underneath the map HP bar with
  // its labels clipped at the container edge. Selectors rather than a number so
  // the reserve stays MEASURED, like --map-hp-reserve, and cannot drift from
  // the CSS that positions them.
  bottomReserveSelectors = null,
} = {}) {
  // Active skill-tree region. 'main' is the default tree; clicking the "Regimen"
  // portal swaps to the self-contained 'htn' region (full teleport, in-place —
  // the whole node set is already in the one loaded definition, so this is a
  // client-side filter, NOT a fetch; ports DrugLabMap.navigateRegion's state
  // machine without its server call). Every SkillTreeViewport mount (dashboard,
  // anon landing/demo, harness) gets region-swap for free via this default.
  const [activeRegion, setActiveRegion] = useState(initialRegion);
  // hoverNodeToast intentionally NOT pulled: the transient hover-peek (which
  // rendered the orphaned compact drug card) is retired 2026-07-12 — click opens
  // the movable float toast, and that is now the sole node-detail surface.
  const { openNodeToast, currentNodeId } = useSkillTreeToast();
  const bodyRef = useRef(null);
  // Hold the body's full DOMRect (viewport coords) so the orb-tray overlap
  // computation is purely a function of two reactive state values — no
  // inline ref reads inside useMemo (which would miss the bodyRef.current
  // mount race on first render).
  const [bodyRect, setBodyRect] = useState(null);

  // Live MapOrbTray rect — converts to "how many CSS pixels at the bottom
  // of THIS body element are occluded by the tray". Updates automatically
  // when the tray's pin state changes its height.
  const orbTrayRect = useMapOrbTrayRect();
  const orbTrayReservePx = useMemo(() => {
    if (!orbTrayRect || !bodyRect) return 0;
    const overlap = bodyRect.bottom - orbTrayRect.top;
    // 2026-05-24: breathing margin dropped from 8 → 0 per Edward's
    // "less margin with the orb tray" pass. KEYSTONE_RESERVE in
    // skillTreeLayout.js is the load-bearing "keep circle above tray"
    // guard; this knob only governed extra label gap.
    return Math.max(0, overlap);
  }, [orbTrayRect, bodyRect]);

  // Host-declared bottom chrome (see the bottomReserveSelectors prop). Same
  // arithmetic as the orb tray: how many CSS pixels at the bottom of THIS body
  // are occluded. Measured live and re-measured when the chrome mounts, moves
  // or resizes, mirroring MapHpIndicator's --map-hp-reserve observer pair.
  const reserveKey = useMemo(
    () => (Array.isArray(bottomReserveSelectors) ? bottomReserveSelectors.join(',') : ''),
    [bottomReserveSelectors],
  );
  const [hudReservePx, setHudReservePx] = useState(0);
  useEffect(() => {
    const el = bodyRef.current;
    if (!reserveKey || !el) {
      setHudReservePx(0);
      return undefined;
    }
    // A gap so the lowest label clears the chrome visibly rather than sitting
    // flush against it. 12px matches MapHpIndicator's horizontal GAP.
    const GAP = 12;
    const scope = el.closest('.deck-map-area') || document;

    const measure = () => {
      const bottom = el.getBoundingClientRect().bottom;
      let worst = 0;
      reserveKey.split(',').forEach((selector) => {
        scope.querySelectorAll(selector).forEach((node) => {
          const rect = node.getBoundingClientRect();
          if (rect.height <= 0 || rect.width <= 0) return;
          // Ignore anything spanning the whole slot (scrims, stage modals):
          // those are transient covers, not chrome to lay out around.
          if (rect.height >= el.getBoundingClientRect().height * 0.9) return;
          worst = Math.max(worst, bottom - rect.top);
        });
      });
      setHudReservePx(worst > 0 ? Math.round(worst + GAP) : 0);
    };

    // The subtree observer below fires on every tree re-render, so coalesce to
    // one measurement per frame: an unthrottled read-then-write per mutation is
    // a layout thrash on a canvas with ~50 nodes.
    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => { frame = 0; measure(); });
    };

    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    reserveKey.split(',').forEach((selector) => {
      scope.querySelectorAll(selector).forEach((node) => ro.observe(node));
    });
    const mo = new MutationObserver(schedule);
    if (scope.nodeType === 1) mo.observe(scope, { childList: true, subtree: true });
    measure();
    window.addEventListener('resize', schedule);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener('resize', schedule);
    };
  }, [reserveKey]);

  // One number reaches the layout. The tallest claim on the bottom band wins;
  // reserving both would double-count hosts that have a tray AND other chrome.
  const bottomReservePx = Math.max(orbTrayReservePx, hudReservePx);

  // Measure the body so the SVG renders sized to the slot AND the orb-tray
  // overlap calc has live viewport-space body coords. Fires on initial
  // mount, every body resize, and on window scroll/resize (so viewport
  // coords stay accurate when the dashboard reflows above).
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return undefined;
    const update = () => setBodyRect(el.getBoundingClientRect());
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, []);

  useEffect(() => {
    onRegionChange?.(activeRegion);
  }, [activeRegion, onRegionChange]);

  const query = useQuery({
    queryKey: ['player', 'skill-tree'],
    queryFn: fetchSkillTreeState,
    staleTime: 30_000,
  });

  // Reservoir is the source of truth for card ownership (matches
  // SkillTreeNodeDetailModal). Used to override skill-tree node status to
  // 'completed' for owned cards so ProgressMap renders them with the
  // canonical green glow + white checkmark.
  const reservoirQuery = useQuery({
    queryKey: ['golden', 'reservoir'],
    queryFn: getReservoir,
    staleTime: 60_000,
  });


  const data = query.data || {};
  const userProgress = data.user_progress || { node_states: data.node_states || {} };

  // Edward 2026-05-28: green check = EQUIPPED (live in loadout), not merely
  // owned/claimed. The set passed to ProgressMap's ownedSlugs promotes nodes
  // to 'completed' (green check), so it must only contain currently-equipped
  // drug slugs — reservoir-owned-but-not-equipped now correctly drops to
  // 'active' via mapStatus('unlocked') instead.
  const equippedDrugs = useMemo(
    () => new Set((data.equipped_drugs || []).map((s) => String(s).toLowerCase())),
    [data.equipped_drugs],
  );
  const ownedSlugs = equippedDrugs;

  // Stow prod-hidden nodes (Psilocybin) right at the normalized graph so they drop
  // from the tree AND the ProgressMap it feeds. No-op off prod (dev/preprod keep it).
  const graph = useMemo(
    () => stowProdHiddenNodes(normalizeSkillTreeDefinition(data.tree_definition, userProgress)),
    [data.tree_definition, userProgress],
  );

  // Region-scoped graph. The full definition holds BOTH the main tree
  // (including the __htn_portal__ gateway) and the HTN drug-card region. We
  // filter to the active region BEFORE the layout fn so
  // each region self-scales — the bounds-box (skillTreeLayoutMetrics) is computed
  // per-region, otherwise the HTN cards' separate canvas would shrink the main
  // tree. In the HTN region we append a synthetic back-portal so the player can
  // return to the main tree (mirrors DrugLabMap's back-portal).
  const regionGraph = useMemo(() => {
    if (!graph?.nodes?.length) return graph;
    if (activeRegion === 'htn') {
      // The HTN region is authored fresh as a class-clustered, playable map:
      // drug cards in per-class columns rising from a bottom-center boss, plus a
      // back-portal and synthesized intra-region edges (the definition's edges
      // all point at the main portal). See computeHtnRegionLayout.
      const htnCards = graph.nodes.filter(
        (n) => String(n.region || '').toLowerCase() === 'htn'
          && n.payloadHandler === 'card_unlock',
      );
      const { nodes, edges } = computeHtnRegionLayout(htnCards, { omitBack: lockRegion });
      return { ...graph, nodes, edges };
    }
    // Main region — the default tree (incl. the __htn_portal__ gateway).
    const nodes = graph.nodes.filter((n) => String(n.region || 'main').toLowerCase() === 'main');
    const ids = new Set(nodes.map((n) => n.id));
    const edges = (graph.edges || []).filter((e) => ids.has(e.from) && ids.has(e.to));
    return { ...graph, nodes, edges };
  }, [graph, activeRegion, lockRegion]);

  const slotW = bodyRect ? Math.round(bodyRect.width) : 0;
  const slotH = bodyRect ? Math.round(bodyRect.height) : 0;
  // Normalize the guided-gate whitelist into a Set once (null = no restriction).
  const restrictSet = useMemo(
    () => (Array.isArray(equipWhitelist) && equipWhitelist.length
      ? new Set(equipWhitelist.map(normalizeOwnableSlug))
      : null),
    [equipWhitelist],
  );
  // The real __htn_portal__ gateway lives in the definition (region:"main"), so
  // it renders on every mount now; the legacy synthetic-portal flag stays wired
  // as a fallback for older definitions that predate the Phase-A publish.
  // The whitelist only applies inside the HTN region (its slugs are all HTN
  // drugs), so guard on activeRegion so the main tree is never restricted.
  const mapData = useMemo(
    () => skillTreeToMapData(regionGraph, slotW, slotH, data.equipped_keystone, bottomReservePx, ownedSlugs, !!onPortal, activeRegion === 'htn', activeRegion === 'htn' ? restrictSet : null),
    [regionGraph, slotW, slotH, data.equipped_keystone, bottomReservePx, ownedSlugs, onPortal, activeRegion, restrictSet],
  );


  const handleNodeClick = useCallback((node, event) => {
    // HTN "Regimen" portal — full-teleport region swap IN PLACE (no navigation
    // away, no standalone HtnCombatMap surface). The forward portal (target_region
    // 'htn') enters the HTN region; the synthetic back-portal (target_region
    // 'main', or the __htn_region_back__ id) returns. Never mutates the loadout.
    // (onPortal is still passed so the portal NODE renders, but it is intentionally
    // NOT called: the logged-in HTN surface is this combat-free in-place region
    // swap, never the standalone combat map.)
    if (node?.type === 'htn_portal') {
      const target = node?.target_region
        || node?.metadata?.target_region
        || (node?.id === '__htn_region_back__' ? 'main' : 'htn');
      // Locked region (standalone demo): refuse any swap back to the main tree.
      if (lockRegion && target === 'main') return;
      setActiveRegion(target === 'main' ? 'main' : 'htn');
      return;
    }
    // A node click opens the drug's card as a MOVABLE, fixed-size FLOATING TOAST
    // (Edward 2026-07-09): same 344px card chrome + palette as the equip hotbar's
    // card, but positioned near the node with a drag grip and NO scrim, so the map
    // stays live behind it. Non-readOnly, so the equip action the old anchored card
    // mediated survives (no backend functionality lost). On phone width it degrades
    // to the centered modal. Hover no longer opens a peek (the compact-card hover
    // was retired 2026-07-12; the float toast is the sole node-detail surface).
    // Applies to every skill-tree surface (main tree, standalone /play, landing,
    // harness) via this one shared component.
    openNodeToast(node, event, { asFloat: true });
  }, [openNodeToast, lockRegion]);

  return (
    <div className="skill-tree-viewport">
      <div className="skill-tree-viewport__body" ref={bodyRef}>
        {query.isLoading ? (
          <div className="skill-tree-viewport__state">Loading skill tree</div>
        ) : query.isError ? (
          <div className="skill-tree-viewport__state skill-tree-viewport__state--error">
            {query.error?.response?.data?.detail || query.error?.message || 'Failed to load skill tree'}
          </div>
        ) : mapData ? (
          <ProgressMap mapData={mapData} onNodeClick={handleNodeClick} activeNodeId={currentNodeId} forceClickable />
        ) : (
          <div className="skill-tree-viewport__state">No skill tree data</div>
        )}
        {/* Owned-cards cluster moved to PlayerTray (Dashboard) so it
            persists across all dashboard modes, not just skill-tree mode. */}
      </div>
    </div>
  );
}
